import { useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Popover,
  Typography,
  alpha
} from '@mui/material';

import { styled } from '@mui/material/styles';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import { useAuth } from 'src/contexts/AuthContext';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding: ${theme.spacing(0.5)};
        min-width: auto;
        border-radius: 8px;

        &:hover {
          background: ${alpha(theme.colors.alpha.trueWhite[100], 0.1)};
        }
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(1.5)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
        font-size: 13px;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${alpha(theme.palette.secondary.main, 0.6)};
        font-size: 11px;
`
);

function HeaderUserbox() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut();
      navigate('/auth/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) return null;

  const displayName = user.full_name || user.email?.split('@')[0] || 'User';
  const displayRole = user.role || 'Team Member';

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar
          variant="circular"
          alt={displayName}
          src={user.avatar_url}
          sx={{ width: 32, height: 32, fontSize: 14 }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2
          }
        }}
      >
        <MenuUserBox sx={{ minWidth: 180 }} display="flex" alignItems="center">
          <Avatar
            variant="circular"
            alt={displayName}
            src={user.avatar_url}
            sx={{ width: 36, height: 36 }}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
          <UserBoxText>
            <UserBoxLabel variant="body1">{displayName}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {displayRole}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <Box sx={{ p: 1 }}>
          <Button
            color="primary"
            fullWidth
            onClick={handleSignOut}
            size="small"
            sx={{
              justifyContent: 'flex-start',
              px: 1.5,
              py: 0.75,
              fontSize: 13
            }}
          >
            <LockOpenTwoToneIcon sx={{ mr: 1, fontSize: 18 }} />
            Sign out
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
